import { useId, useState } from 'react';
import { ArrowDownToLine, ArrowUpRight } from 'lucide-react';
import { BcktUploadSpinner } from '../components/BcktUploadSpinner';
import './branding.css';
import { socialPages, socialAssetVersion, socialAssetRevision } from '../../../server/lib/social-metadata';

const base = '/branding/';
type Option = { value: string; label: string };
type Asset = {
  title: string;
  description: string;
  category: 'logo' | 'banner' | 'preview' | 'badge' | 'comparison' | 'motion' | 'role' | 'social';
  variants: Option[];
  sizes: Option[];
  file: (variant: string, size: string) => string;
  vector?: (variant: string, size: string) => string;
  psd?: string;
};
const tones = [{ value: 'dark', label: 'Dark background' }, { value: 'light', label: 'Light background' }];
const artworkTones = [{ value: 'dark', label: 'Black · transparent' }, { value: 'light', label: 'White · transparent' }];
const logoTones = [...artworkTones, { value: 'lime', label: 'Lime' }];
const banners: Asset[] = ([
  ['brand-pure', 'The essentials', 'Our symbol and wordmark. Nothing extra.'],
  ['brand-minimal', 'BCKT, introduced', 'The brand, the domain and what we do.'],
  ['developers', 'Built for developers', 'Object storage and edge delivery for your next project.'],
  ['gaming-fivem', 'Made for your community', 'Media storage and logs for gaming communities.'],
] as const).map(([slug, title, description]) => ({
  title, description, category: 'banner',
  variants: [...tones, { value: 'transparent', label: 'White artwork · transparent' }, { value: 'transparent-dark', label: 'Black artwork · transparent' }],
  sizes: [{ value: '1200x630', label: '1200 × 630' }, { value: '1500x500', label: '1500 × 500' }, { value: '1920x1080', label: '1920 × 1080' }],
  file: (tone, size) => `banners/png/bckt-${slug}-${size}-${tone}.png`,
  vector: (tone, size) => `banners/svg/bckt-${slug}-${size}-${tone}.svg`,
}));
const logos: Asset[] = [
  {
    title: 'The symbol', description: 'The original BCKT mark, sized for everyday use.', category: 'logo', variants: artworkTones,
    sizes: [500, 300, 150, 80].map(size => ({ value: `${size}x${size}`, label: `${size} × ${size} px` })),
    file: (tone, size) => `logos/bckt-logo-${tone}-${size}.png`,
    vector: tone => `logos/bckt-logo-${tone}.svg`,
  },
  ...(['horizontal', 'stacked'] as const).map(orientation => ({
    title: orientation === 'horizontal' ? 'Horizontal wordmark' : 'Stacked wordmark',
    description: orientation === 'horizontal' ? 'Symbol and name, side by side.' : 'A centered signature for taller spaces.',
    category: 'logo' as const, variants: logoTones, sizes: [{ value: 'original', label: 'Original resolution' }],
    file: (tone: string) => `logos/bckt-lockup-${orientation}-${tone}.png`,
    vector: (tone: string) => `logos/bckt-lockup-${orientation}-${tone}.svg`,
  })),
  {
    title: 'Large symbol', description: 'A generous canvas with transparent space around the mark.', category: 'logo', variants: logoTones,
    sizes: [{ value: '1096', label: '1096 × 1096 px' }],
    file: tone => `logos/bckt-symbol-${tone}.png`, vector: tone => `logos/bckt-symbol-${tone}.svg`,
  },
];
const previews: Asset[] = [{
  title: 'The collection', description: 'All four banner designs together, ready to review or share.', category: 'preview', variants: tones,
  sizes: [{ value: '3780', label: '3780 × 4080 px' }],
  file: tone => `banners/preview${tone === 'light' ? '-light' : ''}.png`,
}];
const badges: Asset[] = ([['powered', 'Powered by BCKT'], ['supported', 'Supported by BCKT.io'], ['unmetered', 'BCKT | 100% Unmetered CDN']] as const).map(([slug, title]) => ({
  title, description: 'A transparent pill for READMEs and storefronts.', category: 'badge', variants: tones,
  sizes: [{value:'original',label:'Original resolution'}],
  file: tone => `badges/bckt-badge-${slug}-${tone}.png`, vector: tone => `badges/bckt-badge-${slug}-${tone}.svg`,
}));
const comparisons: Asset[] = [{
  title: 'Capacity comparison', description: 'Storage, bandwidth and log retention, side by side.', category: 'comparison', variants: tones,
  sizes: [{value:'1080',label:'1080 × 1080'}],
  file: tone => `comparisons/bckt-capacity-comparison-${tone}.png`, vector: tone => `comparisons/bckt-capacity-comparison-${tone}.svg`,
}];
const motion: Asset[] = [{
  title: 'Upload pulse', description: 'A subtle pulse in GIF or animated SVG. The SVG respects reduced motion preferences.', category: 'motion', variants: tones,
  sizes: [{value:'128',label:'128 × 128'}],
  file: tone => `motion/bckt-upload-spinner-${tone}.gif`, vector: tone => `motion/bckt-upload-spinner-${tone}.svg`,
}];
const discord: Asset[] = [{
  title: 'Official Discord server banner', description: 'Logo and text sit within the middle 50%, clear of the top and bottom overlays.', category: 'banner',
  variants: [{value:'dark',label:'Obsidian'}], sizes: [{value:'1920',label:'1920 × 1080'}],
  file: () => 'discord/bckt-discord-server-1920x1080.png', vector: () => 'discord/bckt-discord-server-1920x1080.svg',
}];
// Bump this whenever regenerated artwork changes; immutable static caching then
// cannot keep an older feature card alive under the same URL.
const featureAssetVersion = '20260914-edge-transparent-v1';
const features: Asset[] = ([['media-cdn','Media CDN'],['log-observatory','Log Observatory'],['migration','Migration']] as const).map(([slug,title])=>({
  title, description: 'An individual feature card for community posts and social sharing.', category:'banner',
  variants:[{value:'dark',label:'Obsidian'}], sizes:[{value:'1200',label:'1200 × 630'}],
  file:()=>`features/bckt-feature-${slug}-1200x630.png?v=${featureAssetVersion}`, vector:()=>`features/bckt-feature-${slug}-1200x630.svg?v=${featureAssetVersion}`,
}));
const roles: Asset[] = ([['team','Team / Founder'],['partner','Partner / Creator'],['customer','Customer / Subscriber']] as const).map(([slug,title])=>({
  title, description:'A transparent role icon, shown at its native 64 px size.', category:'role',
  variants:[{value:'dark',label:'For dark surfaces'}], sizes:[{value:'64',label:'64 × 64'}],
  file:()=>`roles/bckt-role-${slug}-64x64.png`, vector:()=>`roles/bckt-role-${slug}-64x64.svg`,
}));

const socialCards: Asset[] = socialPages.map(page => ({
  title: page.label, description: `Link preview for bckt.io${page.path === '/' ? '' : page.path}.`, category: 'social',
  variants: [{ value: 'dark', label: 'Obsidian' }], sizes: [{ value: '1200x630', label: '1200 × 630' }],
  file: () => `social/bckt-social-${page.slug}-1200x630-${socialAssetVersion}.png?v=${socialAssetRevision}`,
  vector: () => `social/bckt-social-${page.slug}-1200x630-${socialAssetVersion}.svg?v=${socialAssetRevision}`,
  psd: `social/bckt-social-${page.slug}-1200x630-${socialAssetVersion}.psd?v=${socialAssetRevision}`,
}));

function AssetCard({ asset }: { asset: Asset }) {
  const id = useId();
  const [variant, setVariant] = useState(asset.variants[0]?.value ?? 'dark');
  const [size, setSize] = useState(asset.sizes[0]?.value ?? 'original');
  const [format, setFormat] = useState(asset.category === 'motion' ? 'svg' : 'webp');
  const rasterFile = asset.file(variant, size);
  const psdFile = asset.psd ?? rasterFile.replace('/png/', '/').replace(/\.(png|gif)(?=\?|$)/, '.psd');
  const previewFile = asset.category === 'motion' ? rasterFile : rasterFile.replace('/png/', '/webp/').replace(/\.png(?=\?|$)/, '.webp');
  const file = format === 'psd' ? psdFile : format === 'svg' && asset.vector ? asset.vector(variant, size)
    : format === 'webp' ? rasterFile.replace('/png/', '/webp/').replace(/\.png(?=\?|$)/, '.webp') : rasterFile;
  const lightSurface = asset.category === 'logo' ? variant === 'dark' : variant === 'light' || variant === 'transparent-dark';
  const transparent = ['logo','badge','motion','role'].includes(asset.category) || variant.startsWith('transparent');
  return (
    <article className={`branding-asset branding-asset--${asset.category}`} aria-labelledby={`${id}-title`}>
      <div className={`branding-asset__stage ${lightSurface ? 'is-light' : 'is-dark'} ${transparent ? 'is-transparent' : ''}`}>
        <picture>
          {asset.category === 'motion' && asset.vector && <source media="(prefers-reduced-motion: reduce)" srcSet={base + asset.vector(variant, size)} />}
          <img src={base + (asset.category === 'motion' ? (format === 'psd' ? rasterFile : file) : previewFile)} alt={`${asset.title}, ${asset.variants.find(option => option.value === variant)?.label}`} loading="lazy" decoding="async" />
        </picture>
      </div>
      <div className="branding-asset__details">
        <h3 id={`${id}-title`}>{asset.title}</h3>
        <p>{asset.description}</p>
        <div className="branding-asset__controls">
          <label htmlFor={`${id}-variant`}>{asset.category === 'logo' ? 'Artwork' : 'Appearance'}
            <select id={`${id}-variant`} value={variant} onChange={event => setVariant(event.target.value)}>
              {asset.variants.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label htmlFor={`${id}-size`}>Size
            <select id={`${id}-size`} value={size} onChange={event => setSize(event.target.value)}>
              {asset.sizes.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label htmlFor={`${id}-format`}>Format
            <select id={`${id}-format`} value={format} onChange={event => setFormat(event.target.value)}>
              {asset.category === 'motion' ? <option value="gif">GIF</option> : <><option value="png">PNG</option><option value="webp">WebP</option></>}{asset.vector && <option value="svg">SVG</option>}
              <option value="psd">{asset.category === 'motion' ? 'PSD (editable artwork)' : 'PSD (editable)'}</option>
            </select>
          </label>
          <a className="branding-download" href={base + file} download aria-label={`Download ${asset.title}, ${variant}, ${size}, ${format.toUpperCase()}`}>
            <ArrowDownToLine size={16} aria-hidden="true" /> Download
          </a>
        </div>
      </div>
    </article>
  );
}

export function Branding() {
  const [demoProgress, setDemoProgress] = useState(37);
  return (
    <div className="branding-page">
      <header className="branding-intro">
        <h1>A place for<br />our identity.</h1>
        <p>Logos, banners and the details that make us BCKT.<br className="branding-desktop-break" /> Pick your format. Make it part of your world.</p>
        <a className="branding-download branding-download--kit" href={`${base}bckt-branding-kit.zip?v=complete-20260915`} download>
          <ArrowDownToLine size={18} aria-hidden="true" /> Download the brand kit <span>ZIP</span>
        </a>
      </header>
      <nav className="branding-index" aria-label="Brand resource categories">
        <a href="#brand-logos">Logos <span>04</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-banners">Banners <span>04</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-badges">Badges <span>03</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-comparisons">Comparisons <span>01</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-motion">Motion <span>01</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-discord">Discord <span>01</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-features">Feature cards <span>03</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-roles">Role icons <span>03</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-social">Social cards <span>{String(socialCards.length).padStart(2, '0')}</span><ArrowUpRight size={16} aria-hidden="true" /></a>
        <a href="#brand-previews">Previews <span>01</span><ArrowUpRight size={16} aria-hidden="true" /></a>
      </nav>
      {([
        ['logos', 'Logos', 'Our signature, in every shape.', 'Choose dark artwork for light surfaces, or light artwork for dark surfaces. Every logo has a transparent background.', logos],
        ['banners', 'Banners', 'Give BCKT some space.', 'Four designs. Three sizes. Dark, light and transparent versions for headers, posts and communities.', banners],
        ['badges', 'Badges', 'A small sign of support.', 'Embed a badge in a GitHub README or Tebex storefront. Dark variants use light artwork; light variants use dark artwork.', badges],
        ['comparisons', 'Comparisons', 'Put the numbers together.', 'Square graphics for Discord and social posts.', comparisons],
        ['motion', 'Motion', 'An upload in motion.', 'The BCKT mark with a subtle pulse, built for the upload queue.', motion],
        ['discord', 'Discord', 'The official server banner.', 'A centered signature with room for Discord’s interface above and below.', discord],
        ['features', 'Feature cards', 'CDN, logs and migration.', 'Three standalone graphics for the services behind BCKT.', features],
        ['roles', 'Role icons', 'Recognize your community.', 'Team, partner and subscriber icons with transparent backgrounds.', roles],
        ['social', 'Social cards', 'A preview for every page.', 'Official link previews for Discord and social posts, with editable Photoshop documents.', socialCards],
        ['previews', 'Previews', 'See the whole collection.', 'High-resolution contact sheets for a closer look at the complete banner family.', previews],
      ] as const).map(([slug, label, title, description, assets]) => (
        <section className="branding-section" id={`brand-${slug}`} key={slug} aria-labelledby={`brand-${slug}-title`}>
          <header className="branding-section__heading">
            <span className="branding-eyebrow">{label}</span>
            <h2 id={`brand-${slug}-title`}>{title}</h2><p>{description}</p>
          </header>
          <div className="branding-grid">{assets.map(asset => <AssetCard key={asset.title} asset={asset} />)}</div>
          {slug === 'logos' && <div className="branding-fonts">
            <p>Editing a PSD? Install these fonts in Photoshop, or open the TTF files in Photopea before opening the PSD, to keep the original text weights and spacing.</p>
            <a className="branding-download" href={`${base}social/bckt-social-fonts.zip`} download><ArrowDownToLine size={16} aria-hidden="true" /> Download PSD fonts</a>
          </div>}
          {slug === 'motion' && <div className="branding-motion-demo">
            <BcktUploadSpinner size={40} progress={demoProgress} label="Upload animation preview" />
            <label htmlFor="branding-upload-demo">Progress preview · {demoProgress}%
              <input id="branding-upload-demo" type="range" min="0" max="100" value={demoProgress} onChange={event => setDemoProgress(Number(event.target.value))} />
            </label>
          </div>}
        </section>
      ))}
    </div>
  );
}
